/**
 * SpeyBooks - Common UI Utilities & Formatters
 * * Global "toolbox" for data transformation, string manipulation, and 
 * theme-consistent visual mapping. Serves as the final step in the 
 * data-to-display pipeline.
 * * Key Functionality:
 * - Tailwind Class Merging: Wraps 'clsx' for safe conditional styling.
 * - Minor Unit Formatting: Converts integer pence into locale-aware currency 
 * strings (en-GB) using the Intl API.
 * - Semantic Theming: Maps transaction and account statuses to standardized 
 * color tokens for consistent UX throughout the platform.
 * - Temporal Formatting: Standardizes date and time displays via date-fns.
 * * Design Standard:
 * Functions are pure and deterministic to ensure predictable rendering 
 * across the 115k+ LOC frontend.
 * * @module lib/utils
 * @author William Murray
 * @version 5.10.3
 * @copyright (c) 2026 SpeyTech Labs LTD. All rights reserved.
 */

import { clsx, type ClassValue } from 'clsx';
import { format, parseISO } from 'date-fns';

// Class name helper
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Money formatting
export function formatMoney(amount: string | number, currency = 'GBP'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num / 100);
}

// Date formatting
export function formatDate(date: string | Date, formatStr = 'dd MMM yyyy'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr);
}

export function formatDateTime(date: string | Date): string {
  return formatDate(date, 'dd MMM yyyy HH:mm');
}

// Status colors
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    draft: 'bg-surface-tertiary text-content-secondary',
    sent: 'bg-accent-tint text-accent-strong',
    paid: 'bg-accent-tint text-accent-strong',
    partial: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-surface-tertiary text-content-tertiary',
    written_off: 'bg-surface-tertiary text-content-tertiary',
    posted: 'bg-accent-tint text-accent-strong',
    open: 'bg-accent-tint text-accent-strong',
    closed: 'bg-surface-tertiary text-content-secondary',
  };
  return colors[status] || 'bg-surface-tertiary text-content-secondary';
}

// Account type colors
export function getAccountTypeColor(type: string): string {
  const colors: Record<string, string> = {
    asset: 'border border-qual-1/40 bg-qual-1/20 text-qual-1',
    liability: 'border border-qual-2/40 bg-qual-2/20 text-qual-2',
    equity: 'border border-qual-3/40 bg-qual-3/20 text-qual-3',
    revenue: 'border border-qual-4/40 bg-qual-4/20 text-qual-4',
    expense: 'border border-qual-4/40 bg-qual-4/10 text-qual-4',
  };
  return colors[type] || 'bg-surface-tertiary text-content-secondary';
}

// Percentage formatting
export function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
}

// Truncate text
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}
